import { css } from "@emotion/css";
import { CalendarDate } from "@internationalized/date";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DatePickerProps as AriaDatePickerProps,
  Calendar,
  DatePickerStateContext,
  DateValue,
} from "react-aria-components";

import { _DatePicker } from "./assets/_DatePicker";
import { _Group } from "./assets/_Group";
import { DatePickerCalendar } from "./components/DatePickerCalendar";
import { DatePickerInput } from "./components/DatePickerInput";
import { DatePickerToggle } from "./components/DatePickerToggle";
import { useDatePickerStyles } from "./utils";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { _Popover } from "../../_assets/_Popover";
import { _Text } from "../../_assets/_Text";
import { Divider } from "../../divider/Divider";
import { _Dialog } from "../../modal/assets/_Dialog";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export interface DatePickerProps
  extends Omit<
    AriaDatePickerProps<any>,
    | "validate"
    | "className"
    | "style"
    | "minValue"
    | "maxValue"
    | "placeholderValue"
  > {
  label: string;
  errors?: FieldErrorsProps["children"];
  isRequired?: boolean;
  testID: string;
  info?: React.ReactNode;
  isStatic?: boolean;
  isFullWidth?: boolean;
  description?: string;
  isClearable?: boolean;
  minValue?: CalendarDate | null;
  maxValue?: CalendarDate | null;
  placeholderValue?: CalendarDate | null;
}

export function DatePicker(props: DatePickerProps) {
  const {
    label,
    errors,
    minValue,
    maxValue,
    placeholderValue,
    defaultValue,
    isRequired,
    testID,
    isStatic,
    info,
    isFullWidth,
    description,
    isClearable,
    ...rest
  } = props;
  const styles = useDatePickerStyles();

  return (
    <_DatePicker
      {...rest}
      className={css({
        gap: isStatic ? undefined : "var(--space-2)",
        alignItems: isStatic ? "center" : "initial",
        width: isStatic || isFullWidth ? "100%" : undefined,
        minWidth: 0,
      })}
      data-testid={testID}
      minValue={minValue as unknown as DateValue}
      maxValue={maxValue as unknown as DateValue}
      placeholderValue={placeholderValue as unknown as DateValue}
    >
      {!isStatic && (
        <FormLabelRAC info={info} isRequired={isRequired}>
          {label}
        </FormLabelRAC>
      )}
      <div
        className={css({
          boxSizing: "border-box",
          width: isStatic || isFullWidth ? "100%" : "200px",
          overflow: "hidden",
          padding: isStatic ? "var(--space-3) var(--space-4)" : undefined,
        })}
      >
        <_Group isInvalid={!!errors}>
          <DatePickerInput />
          {isClearable ? (
            <DatePickerClearButton
              testID={testID}
              isDisabled={props.isDisabled}
              isReadOnly={props.isReadOnly}
            />
          ) : null}
          <DatePickerToggle isStatic={isStatic} isDisabled={props.isDisabled} />
        </_Group>
      </div>
      {description && (
        <_Text style="input/helper" color="text-weak" slot="description">
          {description}
        </_Text>
      )}
      <FieldErrors>{errors}</FieldErrors>
      {isStatic ? (
        <>
          <Divider className={css({ width: "100%" })} />
          <Calendar className={styles.calendar}>
            <DatePickerCalendar />
          </Calendar>
        </>
      ) : (
        <_Popover className={styles.popover}>
          <_Dialog width="288">
            <Calendar className={styles.calendar}>
              <DatePickerCalendar />
            </Calendar>
          </_Dialog>
        </_Popover>
      )}
    </_DatePicker>
  );
}

function DatePickerClearButton({
  testID,
  isDisabled,
  isReadOnly,
}: {
  testID: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
}) {
  const { t } = useTranslation();
  const state = useContext(DatePickerStateContext);
  const styles = useDatePickerStyles();

  if (!state?.value) {
    return null;
  }

  return (
    <Button
      slot={null}
      aria-label={t("Clear")}
      data-testid={`${testID}-clear`}
      className={styles.toggle}
      isDisabled={isDisabled || isReadOnly}
      onPress={() => state.setValue(null)}
    >
      <Icon color="icon-weak" name="cancel" />
    </Button>
  );
}
